import { useCallback, useEffect, useState } from "react";
import { api, errDetail } from "../../lib/api";
import { useApp } from "../../lib/store";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Download, ScrollText } from "lucide-react";
import { toast } from "sonner";

export default function AuditLog() {
  const { t } = useApp();
  const [rows, setRows] = useState([]);
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [f, setF] = useState({ actor_id: "", event: "", date_from: "", date_to: "", q: "" });

  const query = useCallback(() => {
    const p = new URLSearchParams({ limit: 300 });
    Object.entries(f).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p;
  }, [f]);

  const load = useCallback(() => {
    api.get(`/iam/audit?${query().toString()}`).then((r) => setRows(r.data.items || [])).catch((e) => toast.error(errDetail(e)));
  }, [query]);

  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); }, [load]);
  useEffect(() => {
    api.get("/iam/audit/events").then((r) => setEvents(r.data || [])).catch(() => {});
    api.get("/iam/users?page_size=200").then((r) => setUsers(r.data.items || [])).catch(() => {});
  }, []);

  const exportCsv = async () => {
    const p = query();
    p.set("format", "csv");
    const r = await api.get(`/iam/audit?${p.toString()}`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = "iam-audit.csv";
    a.click();
  };

  const fmtVal = (v) => {
    if (!v || (typeof v === "object" && !Object.keys(v).length)) return "–";
    const s = JSON.stringify(v);
    return s.length > 160 ? `${s.slice(0, 160)}…` : s;
  };

  return (
    <div className="space-y-4" data-testid="iam-audit-page">
      <div className="flex flex-wrap items-center gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1B2A4A] flex items-center gap-2" style={{ fontFamily: "Work Sans" }}>
            <ScrollText className="h-6 w-6 text-[#E8871E]" />{t("iam_audit")}
          </h1>
          <p className="text-xs text-gray-500">{t("iam_audit_sub")}</p>
        </div>
        <Button size="sm" variant="outline" className="ml-auto" onClick={exportCsv} data-testid="audit-export-btn">
          <Download className="h-3.5 w-3.5 mr-1" />{t("iam_audit_export")}
        </Button>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-3 grid grid-cols-1 sm:grid-cols-5 gap-2" data-testid="audit-filters">
        <Select value={f.actor_id || "any"} onValueChange={(v) => setF((x) => ({ ...x, actor_id: v === "any" ? "" : v }))}>
          <SelectTrigger className="h-9 text-xs" data-testid="audit-actor-filter"><SelectValue placeholder={t("iam_audit_filter_actor")} /></SelectTrigger>
          <SelectContent><SelectItem value="any">{t("iam_audit_filter_actor")}: {t("all")}</SelectItem>
            {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.event || "any"} onValueChange={(v) => setF((x) => ({ ...x, event: v === "any" ? "" : v }))}>
          <SelectTrigger className="h-9 text-xs" data-testid="audit-event-filter"><SelectValue placeholder={t("iam_audit_event")} /></SelectTrigger>
          <SelectContent><SelectItem value="any">{t("iam_audit_event")}: {t("all")}</SelectItem>
            {events.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="date" className="h-9 text-xs" value={f.date_from} onChange={(e) => setF((x) => ({ ...x, date_from: e.target.value }))} data-testid="audit-from-filter" />
        <Input type="date" className="h-9 text-xs" value={f.date_to} onChange={(e) => setF((x) => ({ ...x, date_to: e.target.value }))} data-testid="audit-to-filter" />
        <Input className="h-9 text-xs" placeholder={t("search")} value={f.q} onChange={(e) => setF((x) => ({ ...x, q: e.target.value }))} data-testid="audit-q-filter" />
      </div>

      <p className="text-xs text-gray-500">{rows.length}</p>

      <div className="space-y-2" data-testid="audit-list">
        {rows.map((r) => (
          <div key={r.id} className="rounded-xl border border-gray-200 bg-white p-3" data-testid={`audit-row-${r.id}`}>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Badge className="bg-[#1B2A4A] text-white hover:bg-inherit text-[10px]">{r.event}</Badge>
              <span className="font-medium">{r.actor_name}</span>
              <span className="text-gray-400">→</span>
              <span>{r.target_name || r.target_id}</span>
              <span className="text-gray-400">({r.target_type})</span>
              <span className="ml-auto font-mono text-gray-400">{(r.at || "").slice(0, 19).replace("T", " ")}</span>
            </div>
            {(Object.keys(r.before || {}).length > 0 || Object.keys(r.after || {}).length > 0) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2 text-[11px]">
                <div className="rounded bg-red-50/60 border border-red-100 p-2">
                  <p className="text-red-700 font-semibold mb-0.5">{t("iam_audit_before")}</p>
                  <code className="break-all text-gray-600">{fmtVal(r.before)}</code>
                </div>
                <div className="rounded bg-emerald-50/60 border border-emerald-100 p-2">
                  <p className="text-emerald-700 font-semibold mb-0.5">{t("iam_audit_after")}</p>
                  <code className="break-all text-gray-600">{fmtVal(r.after)}</code>
                </div>
              </div>
            )}
            {r.note && <p className="text-[11px] text-gray-500 mt-1.5">{r.note}</p>}
          </div>
        ))}
        {!rows.length && <p className="text-sm text-gray-400 py-6 text-center">–</p>}
      </div>
    </div>
  );
}
